import { Link } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

// ────────────────────────────────────────────────────────────────────────────
// LockedFeatureCard — the inline upsell shown when a page or section requires
// a paid plan the tenant doesn't have.
//
// Pages should render this INSTEAD of triggering the global 402 upgrade modal
// on mount (which used to happen any time a free tenant landed on a gated
// page like /developer or /audit-log). The card explains what's locked, why,
// and links to /billing — way better UX than a popup that blocks navigation.
//
// Compact variant is for embedding in widget bodies (e.g. the AI chat
// drawer) where there isn't room for the highlights list.
// ────────────────────────────────────────────────────────────────────────────

const TIER_HIGHLIGHTS: Record<string, string[]> = {
  Starter: [
    'Auto-merge duplicates',
    'Public REST API + webhooks',
    'Email templates & campaigns',
    'Advanced reports',
  ],
  Growth: [
    'Everything in Starter',
    'AI Assistant + drafts + smart alerts',
    'WhatsApp messaging & automation',
    'Audit log',
  ],
  Enterprise: [
    'Everything in Growth',
    'Google Calendar + Zoom sync',
    'Signed Data Processing Agreement',
    '99.5% uptime SLA',
  ],
};

export function LockedFeatureCard({
  icon, title, description, planName, requiredTier, highlights, compact = false,
}: {
  icon?: React.ReactNode;
  title: string;
  description: string;
  planName: string;
  requiredTier: string;
  highlights?: string[];
  compact?: boolean;
}) {
  // Default to the canonical highlights for the requested tier so callers
  // don't have to repeat them. Pass `highlights={[]}` to suppress the list.
  const list = highlights ?? TIER_HIGHLIGHTS[requiredTier] ?? [];

  return (
    <Card>
      <CardContent className={`text-center ${compact ? 'p-6' : 'p-8 sm:p-10'}`}>
        <div className="relative inline-block mb-5">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 blur-xl opacity-30" />
          <div className={`relative ${compact ? 'h-12 w-12' : 'h-16 w-16'} rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/60`}>
            <Lock className={compact ? 'h-6 w-6 text-white' : 'h-8 w-8 text-white'} />
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1 text-[11px] font-bold text-[#6366F1] uppercase tracking-wider mb-3">
          <Sparkles className="h-3 w-3" />
          {requiredTier} plan feature
        </div>
        <h3 className={`font-bold text-slate-900 mb-2 flex items-center justify-center gap-2 ${compact ? 'text-lg' : 'text-xl'}`}>
          {icon}
          {title}
        </h3>
        <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">{description}</p>
        <p className="text-xs text-slate-500 mt-3">
          You're on the <strong className="text-slate-700">{planName}</strong> plan. Upgrade to <strong className="text-[#6366F1]">{requiredTier}</strong> to unlock.
        </p>
        {list.length > 0 && !compact && (
          <ul className="mt-6 space-y-2 max-w-sm mx-auto text-left">
            {list.map((h) => (
              <li key={h} className="flex items-center gap-2.5 text-sm text-slate-700">
                <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/billing"
          className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-xl bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          Upgrade to {requiredTier} <ArrowRight className="h-4 w-4" />
        </Link>
      </CardContent>
    </Card>
  );
}

/** Tiny spinner card used while the plan info is still loading on first paint. */
export function SectionLoadingCard() {
  return (
    <Card>
      <CardContent className="p-12 flex items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </CardContent>
    </Card>
  );
}
